import type { ReactElement, ReactNode } from 'react';
import { Banner } from './Banner';
import { ContractViolationError, errorMessageRu } from '../api/errors';

/**
 * Structural view of a react-query result. Kept structural (rather than
 * importing `UseQueryResult`) so tests can drive the component with a plain
 * object and so it can wrap anything else that loads.
 */
export interface QueryLike<T> {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly data: T | undefined;
  readonly refetch?: () => unknown;
}

export interface QueryStateProps<T> {
  readonly query: QueryLike<T>;
  /** Used in the loading and error copy, e.g. «панель управления». */
  readonly what: string;
  readonly children: (data: T) => ReactNode;
}

export function errorTitleRu(error: unknown): string {
  return error instanceof ContractViolationError
    ? 'Несовместимый ответ сервера'
    : 'Не удалось загрузить данные';
}

/**
 * Loading / error / ready gate.
 *
 * A schema mismatch is surfaced here as an explicit, named failure — the
 * component tree below never runs, so no screen can render `undefined` in a
 * price field. The operator sees what went wrong and can retry.
 */
export function QueryState<T>({ query, what, children }: QueryStateProps<T>): ReactElement {
  if (query.isError) {
    return (
      <div className="stack stack--tight">
        <Banner tone="danger" title={errorTitleRu(query.error)}>
          {errorMessageRu(query.error)}
        </Banner>
        {query.refetch !== undefined && (
          <div className="row">
            <button
              type="button"
              className="btn"
              onClick={() => {
                void query.refetch?.();
              }}
            >
              Повторить запрос
            </button>
          </div>
        )}
      </div>
    );
  }

  if (query.isPending || query.data === undefined) {
    return (
      <p className="empty-state" role="status">
        Загрузка: {what}…
      </p>
    );
  }

  return <>{children(query.data)}</>;
}
